/**
 * ROLE BADGE - Affichage et modification du rôle d'un membre d'équipage
 */

import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { CREW_MEMBER_ROLE, CREW_MEMBER_ROLE_LABELS, type CrewMemberRole } from '@/types/crew';

interface RoleBadgeProps {
  /** Rôle actuel du membre */
  role: CrewMemberRole | null | undefined;
  /** Callback appelé quand le rôle change */
  onRoleChange?: (role: CrewMemberRole) => void;
  /** Mode lecture seule (pas de dropdown) */
  readOnly?: boolean;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Affiche le rôle d'un membre d'équipage avec un badge coloré.
 * En mode édition, affiche un dropdown pour changer le rôle.
 * Un membre est TOUJOURS pilote ou copilote.
 */
export function RoleBadge({ role, onRoleChange, readOnly = false, className }: RoleBadgeProps) {
  // Mode lecture seule : afficher juste un badge
  if (readOnly || !onRoleChange) {
    return (
      <span
        className={cn(
          'inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full',
          role === CREW_MEMBER_ROLE.PILOT && 'bg-blue-100 text-blue-700',
          role === CREW_MEMBER_ROLE.COPILOT && 'bg-purple-100 text-purple-700',
          className
        )}
      >
        {role ? CREW_MEMBER_ROLE_LABELS[role] : 'Pilote'}
      </span>
    );
  }

  // Mode édition : afficher un dropdown stylisé comme un badge
  const options = [
    {
      value: CREW_MEMBER_ROLE.PILOT,
      label: CREW_MEMBER_ROLE_LABELS[CREW_MEMBER_ROLE.PILOT],
    },
    {
      value: CREW_MEMBER_ROLE.COPILOT,
      label: CREW_MEMBER_ROLE_LABELS[CREW_MEMBER_ROLE.COPILOT],
    },
  ];

  return (
    <Select
      value={role || CREW_MEMBER_ROLE.PILOT}
      onChange={(e) => {
        onRoleChange(e.target.value as CrewMemberRole);
      }}
      className={cn(
        'h-7 py-0 px-2 text-xs font-medium rounded-full border-0 cursor-pointer',
        role === CREW_MEMBER_ROLE.PILOT && 'bg-blue-100 text-blue-700',
        role === CREW_MEMBER_ROLE.COPILOT && 'bg-purple-100 text-purple-700',
        !role && 'bg-blue-100 text-blue-700',
        className
      )}
      options={options}
    />
  );
}
